import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, formatApiError } from "@/App";
import { useVideoUrl } from "@/hooks/useVideoUrl";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { 
  ArrowLeft, 
  ArrowRight,
  Play, 
  Pause,
  SkipBack,
  SkipForward,
  Plus,
  Loader2,
  Save
} from "lucide-react";
import { toast } from "sonner";

const AspectRatioPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [project, setProject] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedAspects, setSelectedAspects] = useState([]);
  const [customAspect, setCustomAspect] = useState("");
  const [mode, setMode] = useState("automated");
  const [currentScene, setCurrentScene] = useState(0);
  const [cropX, setCropX] = useState(50);
  const [cropY, setCropY] = useState(50);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const aspects = [
    { id: "1:1", label: "1:1", desc: "Square", icon: "1x1" },
    { id: "9:16", label: "9:16", desc: "Portrait / Stories", icon: "9x16" },
    { id: "4:5", label: "4:5", desc: "Instagram Feed", icon: "4x5" },
    { id: "16:9", label: "16:9", desc: "Landscape / YouTube", icon: "16x9" },
  ];

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, scenesRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/scenes`)
      ]);
      setProject(projectRes.data);
      setScenes(scenesRes.data);
      if (projectRes.data.aspect_ratios?.length) setSelectedAspects(projectRes.data.aspect_ratios);
      if (projectRes.data.crop_mode) setMode(projectRes.data.crop_mode);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!videoRef.current) return;
    const handler = () => setCurrentTime(videoRef.current?.currentTime || 0);
    videoRef.current.addEventListener("timeupdate", handler);
    return () => videoRef.current?.removeEventListener("timeupdate", handler);
  }, [project]);

  const toggleAspect = (id) => {
    setSelectedAspects(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  };

  const addCustom = () => {
    if (!customAspect.match(/^\d+:\d+$/)) { toast.error("Format: W:H (e.g. 3:2)"); return; }
    if (!selectedAspects.includes(customAspect)) setSelectedAspects([...selectedAspects, customAspect]);
    setCustomAspect("");
  };

  const apply = async () => {
    if (selectedAspects.length === 0) { toast.error("Select at least one ratio"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/aspect-ratios`, {
        project_id: projectId,
        aspect_ratios: selectedAspects,
        mode
      });
      toast.success("Aspect ratios saved");
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.pause();
    else videoRef.current.play();
    setPlaying(!playing);
  };

  const seekToScene = (idx) => {
    setCurrentScene(idx);
    if (videoRef.current && scenes[idx]) {
      videoRef.current.currentTime = scenes[idx].start_time;
    }
  };

  const { videoUrl: videoStreamUrl } = useVideoUrl(
    projectId, "source", !!project?.source_video
  );

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />
      <Loader2 className="w-6 h-6 animate-spin text-cyan-400/40" />
    </div>
  );

  // Calculate crop overlay dimensions for preview
  const getCropStyle = (ar) => {
    const [w, h] = ar.split(":").map(Number);
    const ratio = w / h;
    const srcRatio = (project?.video_width || 16) / (project?.video_height || 9);
    let ow, oh;
    if (ratio > srcRatio) { ow = 100; oh = (srcRatio / ratio) * 100; }
    else { oh = 100; ow = (ratio / srcRatio) * 100; }
    return {
      width: `${ow}%`,
      height: `${oh}%`,
      left: `${(100 - ow) * (cropX / 100)}%`,
      top: `${(100 - oh) * (cropY / 100)}%`,
    };
  };

  return (
    <div className="min-h-screen" data-testid="aspect-ratio-page">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />

      <header className="border-b border-cyan-500/10 backdrop-blur-sm bg-[#020a14]/60">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button onClick={() => navigate(`/project/${projectId}`)} className="text-cyan-400/40 hover:text-cyan-300 transition-colors" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="font-['Rajdhani'] text-lg tracking-wide">ASPECT RATIOS</h1>
              <p className="text-sm text-cyan-200/30">{project?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button onClick={apply} className="h-10 px-6 rounded-sm btn-primary font-['Rajdhani'] tracking-wider text-sm" disabled={saving || selectedAspects.length === 0} data-testid="save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />SAVE</>}
            </Button>
            <Button onClick={() => navigate(`/project/${projectId}/restripe`)} variant="outline" className="h-10 px-6 rounded-sm btn-outline-blue font-['Rajdhani'] tracking-wider text-sm" data-testid="continue-btn">
              CONTINUE <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Controls */}
          <div className="space-y-8">
            <div>
              <h2 className="text-sm text-cyan-400/60 mb-6 tracking-widest uppercase">Select Ratios</h2>
              <div className="space-y-3">
                {aspects.map(a => (
                  <label key={a.id} className={`flex items-center gap-4 p-4 border rounded-sm cursor-pointer transition-all ${selectedAspects.includes(a.id) ? 'border-cyan-500/40 bg-cyan-500/[0.05] shadow-[0_0_15px_rgba(0,180,255,0.1)]' : 'border-cyan-500/10 hover:border-cyan-500/20'}`} data-testid={`aspect-${a.id}`}>
                    <Checkbox checked={selectedAspects.includes(a.id)} onCheckedChange={() => toggleAspect(a.id)} />
                    <div className="flex-1">
                      <p className="font-['Rajdhani'] tracking-wide">{a.label}</p>
                      <p className="text-sm text-cyan-200/30">{a.desc}</p>
                    </div>
                    <span className="text-xs text-cyan-500/30 font-mono">{a.icon}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <Input placeholder="Custom e.g. 3:2" value={customAspect} onChange={(e) => setCustomAspect(e.target.value)} className="h-10 rounded-sm" data-testid="custom-aspect-input" />
                <Button variant="outline" size="icon" onClick={addCustom} className="h-10 w-10 rounded-sm btn-outline-blue" data-testid="add-custom-aspect"><Plus className="w-4 h-4" /></Button>
              </div>
              {selectedAspects.filter(a => !aspects.find(p => p.id === a)).length > 0 && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  {selectedAspects.filter(a => !aspects.find(p => p.id === a)).map(a => (
                    <span key={a} className="px-3 py-1 text-sm bg-cyan-500/10 border border-cyan-500/20 rounded-sm text-cyan-200/60">{a}</span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className="text-sm text-cyan-400/60 mb-4 tracking-widest uppercase">Crop Mode</h2>
              <div className="flex gap-2">
                {["automated", "manual"].map(m => (
                  <button key={m} onClick={() => setMode(m)} className={`flex-1 h-10 text-sm capitalize rounded-sm transition-all font-['Rajdhani'] tracking-wider ${mode === m ? 'bg-cyan-500/20 border border-cyan-500/40 text-cyan-200' : 'border border-cyan-500/10 text-cyan-200/40 hover:border-cyan-500/20'}`} data-testid={`mode-${m}`}>{m}</button>
                ))}
              </div>
              <p className="text-xs text-cyan-200/20 mt-2">{mode === "automated" ? "AI centers the crop on detected subjects" : "Manually position the crop per scene"}</p>
            </div>

            {mode === "manual" && selectedAspects.length > 0 && (
              <div className="space-y-4 p-4 card-glass rounded-sm">
                <h3 className="text-sm text-cyan-400/60 tracking-widest uppercase">Manual Position</h3>
                <div>
                  <Label className="text-xs text-cyan-300/60">Horizontal</Label>
                  <Slider value={[cropX]} onValueChange={([v]) => setCropX(v)} max={100} className="mt-2" data-testid="crop-x-slider" />
                </div>
                <div>
                  <Label className="text-xs text-cyan-300/60">Vertical</Label>
                  <Slider value={[cropY]} onValueChange={([v]) => setCropY(v)} max={100} className="mt-2" data-testid="crop-y-slider" />
                </div>
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="lg:col-span-2 space-y-6">
            <div className="aspect-video video-frame rounded-sm relative overflow-hidden">
              {videoStreamUrl ? (
                <>
                  <video
                    ref={videoRef}
                    src={videoStreamUrl}
                    className="w-full h-full object-contain bg-black"
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    data-testid="crop-video-player"
                  />
                  {selectedAspects.map(ar => (
                    <div key={ar} className="crop-overlay" style={getCropStyle(ar)} data-testid={`crop-overlay-${ar}`}>
                      <span className="absolute -top-5 left-1 text-xs text-cyan-400/80 font-mono bg-[#020a14]/80 px-1">{ar}</span>
                    </div>
                  ))}
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-cyan-500/20">
                  <Play className="w-12 h-12" />
                </div>
              )}
            </div>

            <div className="flex items-center justify-center gap-4">
              <button onClick={() => seekToScene(Math.max(0, currentScene - 1))} disabled={currentScene === 0} className="p-2 text-cyan-400/40 hover:text-cyan-300 disabled:opacity-30 transition-colors" data-testid="prev-scene"><SkipBack className="w-5 h-5" /></button>
              <button onClick={togglePlay} className="p-3 text-cyan-400/40 hover:text-cyan-300 transition-colors card-glass rounded-sm" data-testid="play-pause">
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <button onClick={() => seekToScene(Math.min(scenes.length - 1, currentScene + 1))} disabled={currentScene >= scenes.length - 1} className="p-2 text-cyan-400/40 hover:text-cyan-300 disabled:opacity-30 transition-colors" data-testid="next-scene"><SkipForward className="w-5 h-5" /></button>
              <span className="text-sm text-cyan-400/40 font-mono ml-4">{currentTime.toFixed(1)}s</span>
            </div>

            {/* Scenes timeline */}
            <div className="border-t border-cyan-500/10 pt-6">
              <h3 className="text-sm text-cyan-400/60 mb-4 tracking-widest uppercase">Scenes ({scenes.length})</h3>
              {scenes.length > 0 ? (
                <div className="space-y-1">
                  {scenes.map((scene, i) => (
                    <div key={scene.id} onClick={() => seekToScene(i)} className={`flex items-center gap-4 p-3 rounded-sm cursor-pointer transition-all ${currentScene === i ? 'bg-cyan-500/[0.08] border border-cyan-500/20' : 'hover:bg-cyan-500/[0.03] border border-transparent'}`} data-testid={`scene-${i}`}>
                      <span className="text-sm text-cyan-500/30 font-mono w-6">{String(i + 1).padStart(2, '0')}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-cyan-200/60">{scene.start_time.toFixed(1)}s</span>
                          <span className="text-cyan-500/20">-</span>
                          <span className="text-sm text-cyan-200/60">{scene.end_time.toFixed(1)}s</span>
                        </div>
                      </div>
                      <span className="text-xs text-cyan-200/20">{(scene.end_time - scene.start_time).toFixed(1)}s</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-cyan-200/30">No scenes detected. Run analysis first.</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AspectRatioPage;
